import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Chip,
    IconButton,
    TextField,
    Tooltip,
    Collapse,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    Launch,
    Info,
    CheckCircle,
    Error,
    ExpandMore,
    ExpandLess
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getRequest, postRequest } from "../../Fetch/request";
import { getAIFeedbackSync } from "../../Utils/aiUtils";
import { TODAYS_QUESTION_FEEDBACK_PROMPT } from "../../Utils/aiPrompts";
import { useAuth } from '../../hooks/useAuth';

// JSON Schema for structured AI responses
const TODAYS_QUESTION_FEEDBACK_SCHEMA = {
  errors: "string",
  improvements: "string",
  missed_points: "string",
  additional_info_needed: "string",
  overall_assessment: {
    strength_areas: "string",
    weakness_areas: "string",
    complexity_level: "string",
    next_steps: "string"
  }
};

// Note: This component requires REACT_APP_ANTHROPIC_API_KEY environment variable
// Add it to your .env file: REACT_APP_ANTHROPIC_API_KEY=your_api_key_here
const TodaysQuestion = ({ problem, onRefreshProblem, onRefreshCoreSubjectsStats }) => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [question, setQuestion] = useState(null);
    const [userAnswer, setUserAnswer] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGettingAIFeedback, setIsGettingAIFeedback] = useState(false);
    const [aiFeedback, setAiFeedback] = useState(null); // Single AI feedback response
    const [showAIFeedback, setShowAIFeedback] = useState(false);

    const [aiMode] = useState('sync'); // Only sync mode

    // Function to render formatted feedback with proper styling
    const renderFormattedFeedback = (content) => {
        if (typeof content !== 'string') {
            return <Typography variant="body2">{JSON.stringify(content, null, 2)}</Typography>;
        }

        // Split content by lines and process each line
        const lines = content.split('\n');
        const elements = [];
        
        lines.forEach((line, index) => {
            if (line.startsWith('## ')) {
                // Header
                const headerText = line.replace('## ', '').replace(/\*\*/g, '');
                elements.push(
                    <Typography 
                        key={index} 
                        variant="h6" 
                        sx={{ 
                            fontSize: '1.1rem',
                            fontWeight: 'bold', 
                            color: '#008080', 
                            mt: index > 0 ? 2 : 0, 
                            mb: 1 
                        }}
                    >
                        {headerText}
                    </Typography>
                );
            } else if (line.startsWith('**') && line.endsWith('**') && line.length > 4) {
                // Bold line
                const boldText = line.replace(/\*\*/g, '');
                elements.push(
                    <Typography 
                        key={index} 
                        variant="body2" 
                        sx={{ fontWeight: 'bold', color: '#333', mb: 0.5 }}
                    >
                        {boldText}
                    </Typography>
                );
            } else if (line.trim() === '') {
                // Empty line - add spacing
                elements.push(<Box key={index} sx={{ height: '8px' }} />);
            } else if (line.trim()) {
                // Regular text with inline bold formatting
                const parts = line.split(/(\*\*[^*]+\*\*)/);
                const formattedParts = parts.map((part, partIndex) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                        return (
                            <Box 
                                key={partIndex} 
                                component="span" 
                                sx={{ fontWeight: 'bold', color: '#333' }}
                            >
                                {part.replace(/\*\*/g, '')}
                            </Box>
                        );
                    }
                    return part;
                });
                
                elements.push(
                    <Typography 
                        key={index} 
                        variant="body2" 
                        sx={{ 
                            lineHeight: 1.6, 
                            color: '#555', 
                            mb: 0.5,
                            whiteSpace: 'pre-wrap'
                        }}
                    >
                        {formattedParts}
                    </Typography>
                );
            }
        });

        return <Box>{elements}</Box>;
    };

    // Function to get AI feedback from DeepSeek API
    const getAIFeedbackFromAPI = async (questionText, userAnswerText, questionTopic, questionId) => {
        try {
            setIsGettingAIFeedback(true);




            // Use sync mode only
            const feedback = await getAIFeedbackSync(
                TODAYS_QUESTION_FEEDBACK_PROMPT(userAnswerText, questionText),
                `Please evaluate my answer.`,
                null, // userId
                'anthropic', // provider
                null, // model
                TODAYS_QUESTION_FEEDBACK_SCHEMA, // schema
                {
                  maxRetries: 3,
                  onRetry: (attempt, error, delay) => {
                  }
                }
            );
            
            // Parse AI response (handle both JSON schema and text fallback)
            let parsedFeedback = feedback;
            
            try {
                // Handle the structured response from the AI
                if (typeof feedback === 'object' && feedback !== null) {
                    // Build formatted feedback from structured response
                    let formattedFeedback = "";
                    
                    if (feedback.errors) {
                        formattedFeedback += `## 🔍 **Analysis of Your Answer**\n${feedback.errors}\n\n`;
                    }
                    
                    if (feedback.improvements) {
                        formattedFeedback += `## 🚀 **Areas for Improvement**\n${feedback.improvements}\n\n`;
                    }
                    
                    if (feedback.missed_points) {
                        formattedFeedback += `## ❌ **Key Points You Missed**\n${feedback.missed_points}\n\n`;
                    }
                    
                    if (feedback.additional_info_needed) {
                        formattedFeedback += `## 📚 **Additional Information to Consider**\n${feedback.additional_info_needed}\n\n`;
                    }
                    
                    if (feedback.overall_assessment) {
                        const assessment = feedback.overall_assessment;
                        formattedFeedback += `## 📊 **Overall Assessment**\n\n`;
                        
                        if (assessment.strength_areas) {
                            formattedFeedback += `**✅ Strengths:** ${assessment.strength_areas}\n\n`;
                        }
                        
                        if (assessment.weakness_areas) {
                            formattedFeedback += `**⚠️ Areas to Work On:** ${assessment.weakness_areas}\n\n`;
                        }
                        
                        if (assessment.complexity_level) {
                            formattedFeedback += `**📈 Complexity Level:** ${assessment.complexity_level}\n\n`;
                        }
                        
                        if (assessment.next_steps) {
                            formattedFeedback += `## 🎯 **Recommended Next Steps**\n${assessment.next_steps}`;
                        }
                    }
                    
                    parsedFeedback = formattedFeedback || JSON.stringify(feedback, null, 2);
                } else if (typeof feedback === 'string') {
                    parsedFeedback = feedback;
                } else {
                    // Fallback for unexpected response format
                    parsedFeedback = JSON.stringify(feedback, null, 2);
                }
            } catch (error) {
                console.error('Error parsing AI feedback response:', error);
                parsedFeedback = typeof feedback === 'string' ? feedback : JSON.stringify(feedback, null, 2);
            }

            
            // Store single feedback
            const newFeedback = {
                id: Date.now(),
                content: parsedFeedback,
                timestamp: new Date().toISOString()
            };
            
            // Store in localStorage (single feedback)
            const storageKey = `ai-feedback-todays-question-${question?.id || 'default'}`;
            localStorage.setItem(storageKey, JSON.stringify(newFeedback));
            
            setAiFeedback(newFeedback);
            setShowAIFeedback(true);

            // Persist AI feedback to backend (single ai_answer)
            try {
                const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
                if (userId) {
                    const saveBody = {
                        user_id: userId,
                        question_id: questionId || (question?.id || null),
                        ai_answer: newFeedback.content
                    };

                    const saveResult = await postRequest(`${process.env.REACT_APP_URL}/ai-answers/save`, saveBody);
                    if (!saveResult) {
                        // Failed to save AI feedback to backend
                    }
                }
            } catch (saveErr) {
                // Error saving AI feedback to backend
            }
            
        } catch (error) {
                              // Show error message when API fails
            const errorFeedback = {
                id: Date.now(),
                content: `❌ **AI Feedback Error**\n\nSorry, we couldn't get AI feedback at this moment. Please try again later.\n\nError: ${error.message}`,
                timestamp: new Date().toISOString()
            };
            
            // Store error feedback in localStorage
            const storageKey = `ai-feedback-todays-question-${question?.id || 'default'}`;
            localStorage.setItem(storageKey, JSON.stringify(errorFeedback));
            
            setAiFeedback(errorFeedback);
            setShowAIFeedback(true);
        } finally {
            setIsGettingAIFeedback(false);

        }
    };



    // Load AI feedback from localStorage on component mount
    useEffect(() => {
        if (question?.id) {
            const storageKey = `ai-feedback-todays-question-${question.id}`;
            const storedFeedback = JSON.parse(localStorage.getItem(storageKey) || 'null');
            if (storedFeedback) {
                setAiFeedback(storedFeedback);
            }
        }
    }, [question?.id]);



    useEffect(() => {
        // Use the problem prop if provided, otherwise fetch today's question from API
        if (problem) {
            setQuestion(problem);
            setIsLoading(false);
        } else {
            // Fetch today's question from API
            const fetchTodaysQuestion = async () => {
                try {
                    setIsLoading(true);
                    // Removed the API call to /core-questions/question
                    // The question should be provided via props or we'll show a message

                    setQuestion(null);
                } catch (error) {
                } finally {
                    setIsLoading(false);
                }
            };

            fetchTodaysQuestion();
        }
    }, [problem]);

    const handleSubmitAnswer = async () => {
        // Validate answer
        if (!userAnswer.trim()) {
            alert('Please enter your answer before submitting.');
            return;
        }

        // Validate question exists
        if (!question) {
            alert('No question available. Please refresh the page.');
            return;
        }

        // Validate question ID
        if (!question.id) {
            alert('Question ID is missing. Please refresh the page.');
            return;
        }

        // Validate user ID
        const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
        if (!userId) {
            alert('Please login');
            return;
        }

        try {
            setIsSubmitting(true);
            
            // Prepare submission data with all required fields
            const submissionData = {
                userId: userId,           // Required
                questionId: question.id,  // Required
                answer: userAnswer.trim(), // Required
                submittedAt: new Date().toISOString()
            };


            
            const result = await postRequest(`${process.env.REACT_APP_URL}/core-questions/submit`, submissionData);
            
            if (result) {
                
                // Store the answer for AI feedback before clearing
                const submittedAnswer = userAnswer.trim();
                
                // Clear user answer after successful submission
                setUserAnswer('');
                
                // Refresh core subjects stats
                if (onRefreshCoreSubjectsStats) {
                    onRefreshCoreSubjectsStats();
                }
                
                // Automatically get AI feedback after successful submission
                try {
                    await getAIFeedbackFromAPI(question.question, submittedAnswer, question.topic, question.id);
                } catch (verificationError) {
                    // Don't show alert, just log the error
                }
                
                // Note: We don't automatically fetch a new question here
                // User must click "Next Question" to get a new question
            } else {
                alert('Failed to submit answer. Please try again.');
            }
        } catch (error) {
            alert('Error submitting answer. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNextQuestion = async () => {
        // Check authentication first
        if (!isAuthenticated) {
            alert('Please login to get new questions');
            return;
        }

        setUserAnswer('');
        setAiFeedback(null);
        setShowAIFeedback(false);
        setIsGettingAIFeedback(false);
        
        // If we have a refresh callback, use it
        if (onRefreshProblem) {
            onRefreshProblem();
            return;
        }
        
        // Otherwise, show a message that no new question is available
        try {
            setIsLoading(true);
            alert('No new question available. Please refresh the page.');
        } catch (error) {
            alert('Error fetching next question. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const getDifficultyColor = (difficulty) => {
        switch (difficulty.toLowerCase()) {
            case 'easy': return '#4CAF50';
            case 'medium': return '#FF9800';
            case 'hard': return '#F44336';
            default: return '#666666';
        }
    };

    if (isLoading) {
        return (
            <Card sx={{
                boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.1)',
                borderRadius: '15px',
                minHeight: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Typography sx={{ color: '#008080', fontWeight: 'bold' }}>
                    {question ? 'Fetching next question...' : 'Loading today\'s question...'}
                </Typography>
            </Card>
        );
    }

    return (
        <>
            <Card sx={{
                boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.1)',
                borderRadius: '15px'
            }}>
                <CardContent>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '15px' 
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 'bold',
                            color: '#008080',
                        }}>
                            Today's Question
                        </Typography>
                        <Tooltip title="Attempt top interview questions and get AI feedback on your answers" arrow>
                            <IconButton 
                                sx={{ 
                                    color: '#666',
                                    padding: '2px',
                                    '&:hover': { 
                                        backgroundColor: 'rgba(102, 102, 102, 0.1)' 
                                    }
                                }}
                            >
                                <Info fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <IconButton 
                        onClick={() => navigate('/past-submissions')}
                        sx={{ 
                            color: '#008080',
                            padding: '4px',
                            '&:hover': { 
                                backgroundColor: 'rgba(0, 128, 128, 0.1)' 
                            }
                        }}
                    >
                        <Launch fontSize="small" />
                    </IconButton>
                </Box>



                {question ? (
                    <>
                        {/* Question Header */}
                        <Box sx={{ marginBottom: '15px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: '8px' }}>
                                <Chip 
                                    label={question.topic} 
                                    size="small" 
                                    sx={{ 
                                        backgroundColor: '#008080', 
                                        color: 'white',
                                        fontWeight: 'bold'
                                    }} 
                                />
                                <Chip 
                                    label={question.difficulty} 
                                    size="small" 
                                    sx={{ 
                                        backgroundColor: getDifficultyColor(question.difficulty), 
                                        color: 'white',
                                        fontWeight: 'bold'
                                    }} 
                                />
                            </Box>
                            <Typography variant="body1" sx={{
                                fontWeight: '500',
                                lineHeight: 1.6,
                                color: '#333333'
                            }}>
                                {question.question}
                            </Typography>
                        </Box>

                        {/* Answer Text Area */}
                        <Box sx={{ marginBottom: '20px' }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={6}
                                variant="outlined"
                                placeholder="Write your answer here..."
                                value={userAnswer}
                                onChange={(e) => setUserAnswer(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#e0e0e0',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#008080',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#008080',
                                        },
                                    },
                                }}
                            />
                        </Box>



                        {/* Submit and Next Buttons */}
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                onClick={handleNextQuestion}
                                sx={{
                                    borderColor: '#008080',
                                    color: '#008080',
                                    padding: '8px 24px',
                                    borderRadius: '20px',
                                    fontWeight: 'bold',
                                    '&:hover': { 
                                        borderColor: '#006666',
                                        backgroundColor: 'rgba(0, 128, 128, 0.1)'
                                    }
                                }}
                            >
                                Get Next Question
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmitAnswer}
                                disabled={!userAnswer.trim() || isSubmitting}
                                sx={{
                                    backgroundColor: '#008080',
                                    color: 'white',
                                    padding: '8px 24px',
                                    borderRadius: '20px',
                                    fontWeight: 'bold',
                                    '&:hover': { backgroundColor: '#006666' },
                                    '&:disabled': { backgroundColor: '#cccccc' }
                                }}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit Answer'}
                            </Button>
                        </Box>

                                {/* AI Feedback Section */}
        {aiFeedback && (
                            <Box sx={{ marginTop: '20px' }}>
                                <Card sx={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '12px'
                                }}>
                                    <CardContent>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'space-between',
                                            marginBottom: '15px'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CheckCircle sx={{ color: '#4CAF50', fontSize: 20 }} />
                                                                                <Typography variant="h6" sx={{
                                    fontWeight: 'bold',
                                    color: '#333'
                                }}>
                                    AI Feedback
                                </Typography>
                                            </Box>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {/* Expand/Collapse button */}
                                <IconButton
                                    onClick={() => setShowAIFeedback(!showAIFeedback)}
                                    sx={{ color: '#666' }}
                                >
                                    {showAIFeedback ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            </Box>
                                        </Box>
                                        
                                        <Collapse in={showAIFeedback}>
                                            <Box sx={{ 
                                                backgroundColor: 'white',
                                                borderRadius: '8px',
                                                padding: '15px',
                                                border: '1px solid #e0e0e0',
                                                maxHeight: '400px',
                                                overflowY: 'auto',
                                                overflowX: 'hidden'
                                            }}>
                                                <Box sx={{ 
                                                    lineHeight: 1.6,
                                                    color: '#333',
                                                    '& h2': {
                                                        fontSize: '1.1rem',
                                                        fontWeight: 'bold',
                                                        color: '#008080',
                                                        marginBottom: '8px',
                                                        marginTop: '16px'
                                                    },
                                                    '& strong': {
                                                        fontWeight: 'bold',
                                                        color: '#333'
                                                    }
                                                }}>
                                                    {renderFormattedFeedback(aiFeedback.content)}
                                                </Box>
                                            </Box>
                                        </Collapse>
                                        
                                        {/* Helpful message for next steps */}
                                        <Box sx={{ 
                                            marginTop: '15px',
                                            padding: '10px',
                                            backgroundColor: '#e3f2fd',
                                            borderRadius: '8px',
                                            border: '1px solid #2196f3'
                                        }}>
                                            <Typography variant="body2" sx={{
                                                color: '#1976d2',
                                                fontWeight: '500',
                                                textAlign: 'center'
                                            }}>
                                                💡 Review the AI feedback above, then click "Get Next Question" when you're ready for the next challenge!
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        )}

                        {/* AI Feedback Loading */}
                        {isGettingAIFeedback && (
                            <Box sx={{ 
                                marginTop: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2
                            }}>
                                <CircularProgress size={20} sx={{ color: '#008080' }} />
                                <Typography sx={{ color: '#008080', fontWeight: 'bold' }}>
                                    Getting AI feedback...
                                </Typography>
                            </Box>
                        )}
                    </>
                ) : (
                    /* Empty State - No Question Available */
                    <Box sx={{ 
                        textAlign: 'center', 
                        py: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '300px'
                    }}>
                        <Typography variant="h5" sx={{ 
                            color: '#666666', 
                            mb: 2, 
                            fontWeight: 'bold' 
                        }}>
                            Wait for new questions to arrive
                        </Typography>
                        <Typography variant="body1" sx={{ 
                            color: '#999999', 
                            mb: 4, 
                            maxWidth: 400 
                        }}>
                            No questions are available at the moment. New questions will appear here soon.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={handleNextQuestion}
                            sx={{
                                borderColor: '#008080',
                                color: '#008080',
                                padding: '8px 24px',
                                borderRadius: '20px',
                                fontWeight: 'bold',
                                '&:hover': { 
                                    borderColor: '#006666',
                                    backgroundColor: 'rgba(0, 128, 128, 0.1)'
                                }
                            }}
                        >
                            Refresh
                        </Button>
                    </Box>
                )}
            </CardContent>
        </Card>


        </>
    );
};

export default TodaysQuestion; 